// client/src/lib/api/shops-api.ts
// ===========================================
// MÓDULO DE GESTIÓN DE TIENDAS Y CONFIGURACIÓN
// Maneja CRUD de tiendas, configuración de Telegram y tarjetas bancarias.
// Usa apiRequest para inyectar headers y manejar errores de forma tipada.
// ===========================================

import { apiRequest, buildUrlWithParams, ApiError } from "./api-client";
import type { Shop, Owner } from "@shared/schema";
import type { ApiMessageResponse } from "./types";

// -------------------------------------------
// Crear tienda adicional para un owner
// -------------------------------------------
// COMENTARIO: apiRequest maneja la respuesta. Extraemos response.shop para mantener
// la compatibilidad con componentes que esperan directamente el objeto Shop.
export async function createShopForOwner(
  ownerId: string,
  shopData: {
    name: string;
    address: string;
    username: string;
    password: string;
  },
): Promise<Shop> {
  const response = await apiRequest<{ shop: Shop }>(
    `/api/owners/${ownerId}/shops`,
    {
      method: "POST",
      body: JSON.stringify(shopData),
    },
  );
  return response.shop;
}

// -------------------------------------------
// Obtener tiendas de un owner
// -------------------------------------------
export async function getOwnerShops(ownerId: string): Promise<Shop[]> {
  const response = await apiRequest<{ shops: Shop[] }>(
    `/api/owners/${ownerId}/shops`,
  );
  return response.shops;
}

// -------------------------------------------
// Clonar tienda existente
// -------------------------------------------
export async function cloneShop(
  ownerId: string,
  data: {
    sourceShopId: string;
    name: string;
    address: string;
    owner: string;
    cloneOptions: { inventory: boolean; settings: boolean };
  },
): Promise<any> {
  return apiRequest<any>(`/api/owners/${ownerId}/shops/clone`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

// -------------------------------------------
// Actualizar datos de una tienda
// -------------------------------------------
export async function updateShop(
  shopId: string,
  updates: {
    name?: string;
    address?: string;
    owner?: string;
    username?: string;
    password?: string;
  },
): Promise<Shop> {
  const response = await apiRequest<{ shop: Shop }>(`/api/shops/${shopId}`, {
    method: "PATCH",
    body: JSON.stringify(updates),
  });
  return response.shop;
}

// -------------------------------------------
// Eliminar tienda
// -------------------------------------------
export async function deleteShop(shopId: string): Promise<ApiMessageResponse> {
  return apiRequest<ApiMessageResponse>(`/api/shops/${shopId}`, {
    method: "DELETE",
  });
}

// -------------------------------------------
// Actualizar ID de chat de Telegram (Legacy)
// -------------------------------------------
// COMENTARIO: Se mantiene para compatibilidad. Retorna void ya que solo verifica éxito/error.
export async function updateShopTelegram(
  shopId: string,
  telegramChatId: string,
): Promise<void> {
  await apiRequest<void>(`/api/shops/${shopId}/telegram`, {
    method: "PATCH",
    body: JSON.stringify({ telegramChatId }),
  });
}

// -------------------------------------------
// Probar conexión con Telegram
// -------------------------------------------
export async function testTelegramConnection(
  shopId: string,
): Promise<{ success: boolean; message?: string; error?: string }> {
  return apiRequest(`/api/telegram/test?shopId=${encodeURIComponent(shopId)}`);
}

// -------------------------------------------
// Enviar mensaje de prueba a Telegram
// -------------------------------------------
// COMENTARIO: Mantenemos el try/catch para manejar fallos de red o parsing JSON,
// ya que este endpoint puede devolver respuestas no estándar en errores de infraestructura.
export async function sendTestMessage(
  shopId: string,
  chatId: string,
  message: string,
): Promise<{ success: boolean; message?: string; error?: string }> {
  try {
    const response = await apiRequest<{
      success: boolean;
      message?: string;
      error?: string;
    }>("/api/telegram/test-message", {
      method: "POST",
      body: JSON.stringify({ shopId, chatId, message }),
    });
    return response;
  } catch (error: any) {
    if (error instanceof ApiError) {
      return { success: false, error: error.message };
    }
    return { success: false, error: "Error de conexión con el servidor" };
  }
}

// -------------------------------------------
// Configurar Telegram para una tienda
// -------------------------------------------
// COMENTARIO: apiRequest lanza ApiError si falla. El frontend ya maneja el catch,
// por lo que simplificamos retornando directamente la respuesta exitosa.
export async function setTelegramConfig(
  shopId: string,
  config: {
    botToken: string;
    chatId: string;
    enabled: boolean;
    lowStockAlerts?: boolean;
    importantSaleAlerts?: boolean;
    dailySummaryAlerts?: boolean;
    newCustomerAlerts?: boolean;
    importantSaleThreshold?: number;
    dailySummaryTime?: string;
  },
): Promise<ApiMessageResponse> {
  return apiRequest<ApiMessageResponse>(
    `/api/shops/${shopId}/telegram-config`,
    {
      method: "POST",
      body: JSON.stringify(config),
    },
  );
}

// -------------------------------------------
// Obtener configuración de Telegram
// -------------------------------------------
// COMENTARIO: Mantenemos la lógica original: si el backend devuelve 404, retornamos valores por defecto.
// Usamos ApiError para detectar el status 404 sin romper el flujo.
export async function getTelegramConfig(shopId: string): Promise<{
  botToken: string | null;
  chatId: string | null;
  enabled: boolean;
  lowStockAlerts: boolean;
  importantSaleAlerts: boolean;
  dailySummaryAlerts: boolean;
  newCustomerAlerts: boolean;
  importantSaleThreshold: number;
  dailySummaryTime: string;
}> {
  try {
    return await apiRequest(`/api/shops/${shopId}/telegram-config`);
  } catch (error) {
    if (error instanceof ApiError && error.status === 404) {
      return {
        botToken: null,
        chatId: null,
        enabled: false,
        lowStockAlerts: true,
        importantSaleAlerts: true,
        dailySummaryAlerts: true,
        newCustomerAlerts: true,
        importantSaleThreshold: 500,
        dailySummaryTime: "20:00",
      };
    }
    throw error;
  }
}

// -------------------------------------------
// Gestión de Tarjetas Bancarias
// -------------------------------------------
export async function getShopBankCards(shopId: string): Promise<any[]> {
  const response = await apiRequest<{ cards: any[] }>(
    `/api/shops/${shopId}/bank-cards`,
  );
  return response.cards || [];
}

export async function addShopBankCard(
  shopId: string,
  cardData: { cardNumber: string; nickname?: string },
): Promise<any> {
  return apiRequest(`/api/shops/${shopId}/bank-cards`, {
    method: "POST",
    body: JSON.stringify(cardData),
  });
}

export async function updateShopBankCard(
  shopId: string,
  cardId: string,
  cardData: { cardNumber?: string; nickname?: string; isActive?: boolean },
): Promise<any> {
  return apiRequest(`/api/shops/${shopId}/bank-cards/${cardId}`, {
    method: "PATCH",
    body: JSON.stringify(cardData),
  });
}

export async function deleteShopBankCard(
  shopId: string,
  cardId: string,
): Promise<void> {
  return apiRequest<void>(`/api/shops/${shopId}/bank-cards/${cardId}`, {
    method: "DELETE",
  });
}
